<script>
/* eslint-env browser */
/* global FB */
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

import ChannelApi from '../../../../../api/channels';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    PageHeader,
    NextButton,
    ComboBox,
  },
  setup() {
    const { accountId } = useAccount();
    const { replaceInstallationName } = useBranding();
    return {
      accountId,
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      channel: 'facebook',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
    };
  },

  validations: {
    pageName: {
      required,
    },

    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
    comboBoxPageOptions() {
      return this.getSelectablePages.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
    },
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },

  methods: {
    async startLogin() {
      this.hasLoginStarted = true;
      this.hasError = false;
      this.errorStateMessage = '';
      this.errorStateDescription = '';

      try {
        // Validate Facebook App ID before proceeding
        if (!window.chatwootConfig?.fbAppId) {
          throw new Error(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_CONFIG'));
        }

        // Load the SDK
        await this.loadFBsdk();

        // Wait for FB to be initialized
        await this.waitForFBInit();

        // Verify FB is ready before attempting login
        if (!window.FB || !window.fbSDKLoaded) {
          throw new Error('Facebook SDK no se inicializó correctamente');
        }

        // Now safe to attempt login
        this.tryFBlogin();
      } catch (error) {
        this.hasError = true;
        this.hasLoginStarted = false;

        if (error.name === 'ScriptLoaderError') {
          this.errorStateMessage = this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING');
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else if (error.message.includes('App ID')) {
          this.errorStateMessage = error.message;
          this.errorStateDescription = this.$t('INBOX_MGMT.DETAILS.ERROR_FB_CONFIG');
          useAlert(error.message);
        } else {
          // eslint-disable-next-line no-console
          console.error('Facebook initialization error:', error);
          Sentry.captureException(error);
          this.errorStateMessage = this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },

    setPageName(pageId) {
      const page = this.pageList.find(p => p.id === pageId);
      if (page) {
        this.selectedPage = page;
        this.pageName = page.name;
      } else {
        this.selectedPage = { name: null, id: null };
        this.pageName = '';
      }
      this.v$.selectedPage.$touch();
    },

    initChannelAuth(channel) {
      if (channel === 'facebook') {
        this.loadFBsdk();
      }
    },

    runFBInit() {
      try {
        const fbAppId = window.chatwootConfig?.fbAppId;
        const fbApiVersion = window.chatwootConfig?.fbApiVersion || 'v18.0';

        if (!fbAppId) {
          // eslint-disable-next-line no-console
          console.error('Facebook App ID no está configurado');
          return;
        }

        FB.init({
          appId: fbAppId,
          xfbml: true,
          version: fbApiVersion,
          status: true,
        });
        window.fbSDKLoaded = true;
        FB.AppEvents.logPageView();

        // Resolve the init promise if it exists
        if (window.fbInitPromise) {
          window.fbInitResolve();
          window.fbInitPromise = null;
          window.fbInitResolve = null;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing Facebook SDK:', error);
        if (window.fbInitPromise) {
          window.fbInitReject(error);
          window.fbInitPromise = null;
          window.fbInitResolve = null;
          window.fbInitReject = null;
        }
      }
    },

    waitForFBInit() {
      return new Promise((resolve, reject) => {
        // If FB is already initialized, resolve immediately
        if (window.FB && window.fbSDKLoaded) {
          resolve();
          return;
        }

        // If SDK is already loaded but not initialized, initialize it
        if (window.FB && !window.fbSDKLoaded) {
          this.runFBInit();
          // Wait a bit for init to complete
          setTimeout(() => {
            if (window.fbSDKLoaded) {
              resolve();
            } else {
              reject(new Error('Facebook SDK initialization timeout'));
            }
          }, 1000);
          return;
        }

        // Set up promise for async init
        window.fbInitPromise = true;
        window.fbInitResolve = resolve;
        window.fbInitReject = reject;

        // If fbAsyncInit hasn't been called yet, call runFBInit manually
        // This handles the case where the script loads before fbAsyncInit is set
        if (!window.fbAsyncInitCalled) {
          setTimeout(() => {
            if (window.FB && !window.fbSDKLoaded) {
              this.runFBInit();
            }
          }, 100);
        }

        // Timeout after 10 seconds
        setTimeout(() => {
          if (window.fbInitPromise) {
            window.fbInitPromise = null;
            window.fbInitResolve = null;
            window.fbInitReject = null;
            reject(new Error('Facebook SDK initialization timeout'));
          }
        }, 10000);
      });
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin() {
      if (!window.FB || !window.fbSDKLoaded) {
        this.hasError = true;
        this.errorStateMessage = this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
        this.errorStateDescription = 'Facebook SDK no está inicializado';
        return;
      }

      FB.login(
        response => {
          this.hasError = false;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is logged into Facebook, but not your app.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is not logged into Facebook, so we're not sure if
            // they are logged into this app or not.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          scope:
            'pages_manage_metadata,business_management,pages_messaging,instagram_basic,pages_show_list,pages_read_engagement,instagram_manage_messages',
        }
      );
    },

    async fetchPages(_token) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details;
        this.user_access_token = data.user_access_token;
      } catch (error) {
        // Ignore error
      }
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name?.trim(),
      };
    },

    createChannel() {
      this.v$.$touch();
      if (!this.v$.$error) {
        this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
        this.isCreating = true;
        this.$store
          .dispatch('inboxes/createFBChannel', this.channelParams())
          .then(data => {
            router.replace({
              name: 'settings_inboxes_add_agents',
              params: { page: 'new', inbox_id: data.id },
            });
          })
          .catch(() => {
            this.isCreating = false;
          });
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <div
      v-if="!hasLoginStarted"
      class="flex flex-col items-center justify-center h-full text-center"
    >
      <a href="#" @click="startLogin()">
        <img
          class="w-auto h-10 rounded-md"
          src="~dashboard/assets/images/channels/facebook_login.png"
          alt="Facebook-logo"
        />
      </a>
      <p class="py-6">
        {{ replaceInstallationName($t('INBOX_MGMT.ADD.FB.HELP')) }}
      </p>
    </div>
    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
      <form
        v-else
        class="flex flex-col flex-wrap mx-0"
        @submit.prevent="createChannel()"
      >
        <div class="w-full">
          <PageHeader
            :header-title="$t('INBOX_MGMT.ADD.DETAILS.TITLE')"
            :header-content="
              replaceInstallationName($t('INBOX_MGMT.ADD.DETAILS.DESC'))
            "
          />
        </div>
        <div class="w-3/5">
          <div class="w-full mb-2">
            <div class="input-wrap" :class="{ error: v$.selectedPage.$error }">
              <span class="text-n-slate-12 text-start">
                {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE') }}
              </span>
              <ComboBox
                :model-value="selectedPage.id"
                :options="comboBoxPageOptions"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
                :has-error="v$.selectedPage.$error"
                class="[&>div>button]:!bg-n-alpha-black2 mt-1"
                @update:model-value="setPageName"
              />
              <span v-if="v$.selectedPage.$error" class="message mt-0.5">
                {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
              </span>
            </div>
          </div>
          <div class="w-full">
            <label :class="{ error: v$.pageName.$error }">
              {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
              <input
                v-model="pageName"
                type="text"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
                @input="v$.pageName.$touch"
              />
              <span v-if="v$.pageName.$error" class="message">
                {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
              </span>
            </label>
          </div>
          <div class="w-full text-right">
            <NextButton :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')" />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
